import { useEffect, useRef, useState } from 'react';
import createViewer, { type OSDHandle } from './viewer/createViewer';
import CanvasOverlay from './viewer/CanvasOverlay';
import Sidebar from './components/Sidebar/Sidebar';
import { useMapStore } from './store/useMapStore';
import Legend from './components/Legend';
import { readURL, writeURL } from './logic/url';
import OpenSeadragon from 'openseadragon';

export default function App() {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [osd, setOsd] = useState<OSDHandle | null>(null);
  const setViewer = useMapStore((s) => s.setViewer);
  const sidebarOpen = useMapStore((s) => s.sidebarOpen);
  const setSidebarOpen = useMapStore((s) => s.setSidebarOpen);

  useEffect(() => {
    if (!containerRef.current) return;
    const handle = createViewer(containerRef.current);
    setOsd(handle);
    setViewer(handle);
    return () => handle.destroy();
  }, [setViewer]);

  // Apply URL center/zoom once the image is open
  useEffect(() => {
    if (!osd) return;
    const { viewer, getItem } = osd;
    const apply = () => {
      const it = getItem();
      if (!it) return;
      const u = readURL();
      if (u.cx !== undefined && u.cy !== undefined) {
        const imgPt = new OpenSeadragon.Point(u.cx * 8192, u.cy * 6656);
        const vpPt = it.imageToViewportCoordinates(imgPt);
        if (u.iz !== undefined) {
          const vpZoom = it.imageToViewportZoom(u.iz);
          viewer.viewport.zoomTo(vpZoom, vpPt, true);
        }
        viewer.viewport.panTo(vpPt, true);
      }
    };
    viewer.addOnceHandler('open', apply);
    return () => {};
  }, [osd]);

  // Update URL on viewport changes (debounced)
  useEffect(() => {
    if (!osd) return;
    const { viewer, getItem } = osd;
    let raf = 0;
    const onUpdate = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const it = getItem();
        if (!it) return;
        const centerVP = viewer.viewport.getCenter(true);
        const centerImg = it.viewportToImageCoordinates(centerVP);
        const cx = centerImg.x / 8192;
        const cy = centerImg.y / 6656;
        const iz = it.viewportToImageZoom(viewer.viewport.getZoom(true));
        writeURL({ cx, cy, iz });
      });
    };
    viewer.addHandler('animation', onUpdate);
    viewer.addHandler('update-viewport', onUpdate);
    return () => {
      cancelAnimationFrame(raf);
      viewer.removeHandler('animation', onUpdate);
      viewer.removeHandler('update-viewport', onUpdate);
    };
  }, [osd]);

  return (
    <div className="w-screen h-screen overflow-hidden bg-black text-white">
      <div className="absolute top-3 left-3 z-10 px-2 py-1 rounded bg-neutral-900/70 text-xs">
        Warhammer 40K Galaxy Map (v1)
      </div>
      <div ref={containerRef} className="w-full h-full" />
      {osd && <CanvasOverlay osd={osd} />}
      <Sidebar />
      <Legend />
      {!sidebarOpen && (
        <button
          onClick={() => setSidebarOpen(true)}
          className="fixed right-3 top-3 z-30 px-2 py-1 text-xs rounded bg-neutral-900/70 border border-neutral-700 hover:bg-neutral-800"
        >
          Open Panel ▸
        </button>
      )}
    </div>
  );
}
